'use client'

import { useState } from 'react'
import {
  Check,
  Copy,
  Heart,
  KeyRound,
  Laptop,
  ServerCog,
  Sparkles,
  WalletCards,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ContentPage } from '@/components/content-page'

const ZELLE_ADDRESS = 'rick.lopez@icloud.com'

const supportUses = [
  {
    icon: Laptop,
    title: 'Platform testing',
    description:
      'Test hardware and operating-system combinations used to verify NewEmail across desktop environments.',
  },
  {
    icon: ServerCog,
    title: 'Provider compatibility',
    description:
      'Test accounts and provider configurations used to reproduce IMAP and SMTP differences safely.',
  },
  {
    icon: KeyRound,
    title: 'Signing and releases',
    description:
      'Developer certificates, notarization, distribution, and the infrastructure behind signed releases.',
  },
  {
    icon: Sparkles,
    title: 'Development tools',
    description:
      'AI development tokens and software licenses used while building and testing NewEmail.',
  },
]

export default function DonatePage() {
  const [copied, setCopied] = useState(false)

  async function copyZelle() {
    try {
      await navigator.clipboard.writeText(ZELLE_ADDRESS)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <ContentPage
      wide
      eyebrow="// support the project"
      title="Donate to NewEmail"
      description="NewEmail is built independently. Your support helps cover testing, releases, and the tools needed to keep improving the app."
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-6">
        <div className="grid gap-4 md:grid-cols-2">
          <section className="flex flex-col rounded-2xl border border-border bg-card p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-background text-primary">
              <Laptop className="h-5 w-5" />
            </span>
            <h2 className="mt-5 text-2xl font-bold tracking-tight">Help with testing</h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              Unusual mail providers, older desktop hardware, and reproducible account problems
              are especially valuable. Test access can be arranged without sharing your primary
              password or private message content.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Contact us first so we can agree on a safe testing setup and confirm what is useful.
            </p>
            <Button
              className="mt-5 self-start"
              variant="outline"
              nativeButton={false}
              render={
                <a href="mailto:rick@new-email.net?subject=Testing%20support%20for%20NewEmail" />
              }
            >
              Offer testing help
            </Button>
          </section>

          <section className="flex flex-col rounded-2xl border border-primary/30 bg-secondary/50 p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-primary/30 bg-background text-primary">
              <WalletCards className="h-5 w-5" />
            </span>
            <h2 className="mt-5 text-2xl font-bold tracking-tight">Financial donations</h2>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              Financial donations are accepted through Zelle. Contributions help with testing,
              signed-release infrastructure, provider accounts, and the development tools used to
              improve NewEmail.
            </p>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              Donations go directly to the project rather than through a platform that keeps a
              percentage. Thank you for helping keep NewEmail independent.
            </p>
            <div className="mt-5 rounded-xl border border-border bg-card p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Zelle
              </p>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-sm font-semibold text-foreground">
                  {ZELLE_ADDRESS}
                </span>
                <Button type="button" variant="outline" size="sm" onClick={copyZelle}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy address'}
                </Button>
              </div>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              Please include “NewEmail donation” in the Zelle memo.
            </p>
          </section>
        </div>

        <section className="rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-3">
            <Heart className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">What your support pays for</h2>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {supportUses.map((item) => {
              const Icon = item.icon
              return (
                <div
                  key={item.title}
                  className="flex items-start gap-3 rounded-xl bg-secondary/50 p-4"
                >
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <h3 className="font-medium text-foreground">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        <p className="text-center text-sm leading-relaxed text-muted-foreground">
          Thank you for helping make NewEmail better for everyone.
        </p>
      </div>
    </ContentPage>
  )
}
